import { useState, useMemo, useCallback } from 'react';
import { X, TreePine } from 'lucide-react';
import type { TreeViewModalProps } from './types';
import { FlowNode } from './FlowNode';

export function TreeViewModal({ isOpen, onClose, questions, getFollowUpQuestions, questionCTAs, onEditQuestion }: TreeViewModalProps) {
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const rootQuestions = useMemo(() => questions.filter(q => q.is_root).slice().sort((a, b) => a.order_index - b.order_index), [questions]);
  const toggleNode = useCallback((questionId: string) => {
    setExpandedNodes(prev => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(questionId)) {
        newExpanded.delete(questionId);
      } else {
        newExpanded.add(questionId);
      }
      return newExpanded;
    });
  }, []);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex justify-between items-center p-4 sm:p-6 border-b border-gray-200">
          <div>
            <h3 className="text-xl font-bold text-gray-900">Question Flow</h3>
            <p className="text-sm text-gray-500 mt-1">
              Visual representation of questions and their relationship
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-auto p-3 sm:p-6">
          {rootQuestions.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <TreePine className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>No root questions found. Add some questions to see the question flow.</p>
            </div>
          ) : (
            <div>
              <div className="mb-6 p-3 sm:p-4 bg-gray-50 flex flex-wrap items-center gap-4 sm:gap-8">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-4 bg-gray-100 border border-gray-200"></div>
                  <span className="text-xs sm:text-sm text-gray-700">Root question</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-1 h-5 bg-indigo-500"></div>
                  <span className="text-xs sm:text-sm text-gray-700">Level 1 follow-up (L1)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-1 h-5 bg-purple-500"></div>
                  <span className="text-xs sm:text-sm text-gray-700">Level 2 follow-up (L2)</span>
                </div>
              </div>

              <div className="overflow-x-auto">
                <div className="min-w-[480px] space-y-2">
                  {rootQuestions.map((rootQuestion, index) => (
                    <FlowNode
                      key={rootQuestion.id}
                      question={rootQuestion}
                      level={0}
                      hierarchicalNumber={`${index + 1}`}
                      getFollowUpQuestions={getFollowUpQuestions}
                      questionCTAs={questionCTAs}
                      expandedNodes={expandedNodes}
                      onToggle={toggleNode}
                      visitedPath={new Set<string>()}
                      onEditQuestion={onEditQuestion}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="p-4 sm:p-6 border-t border-gray-200">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
            <p className="text-xs sm:text-sm text-gray-500">
              Click "Display follow-up" to expand question branches. Click a question to edit it.
            </p>
            <button onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-800  hover:bg-gray-300">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
